/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import useApi from 'hooks/useApi';
import { get } from 'lodash';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import ServerSideTable from 'components/common/advance-table/ServerSideTable';
import ServerSideTableWrapper from 'components/common/advance-table/ServerSideTableWrapper';
import {
  getDynamicReportdata,
  getDynamicReportdataForWeather,
  getDropdownFilterData
} from 'api/DynamicReportApi';
import { SERVICES } from 'api/common/constants';
import DatePicker from 'components/dashboards/manage-projects/DatePicker';
import ViewComponent from './ViewComponent';
import { debounce } from 'lodash';
import TableComponent from 'screens/common/tanStackTable/Table';
import { isEmpty } from 'lodash';
import {
  Button,
  Card,
  CardBody,
  Col,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import CardDropdown from 'components/common/CardDropdown';
import { useNavigate } from 'react-router-dom';
import FalconCardHeader from 'components/common/FalconCardHeader';
import DeliveryDetailsModal from 'screens/common/tanStackTable/DeliveryDetailsModal';
import { getTenantFromToken } from 'utils/routeUtils';
import { downloadExportFile } from 'api/endpoints/Export';
import DynamicModal from '../tanStackTable/DetailsModal';

const DynamicReportTable = ({
  showFilter,
  setExportData,
  onEdit,
  onAdd,
  onViewDetails,
  apiPath,
  forWeather,
  dropdownFilter,
  dropdownData,
  payloadmode,
  dateFilter,
  district_ids,
  errorMsg,
  responsive,
  cardTittles,
  globalSearch,
  dateRangePicker,
  limit = 25,
  buttons,
  title,
  handleDownload,
  showHeader = true,
  showFooter = true,
  service
}) => {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();
  const apiHook = forWeather
    ? getDynamicReportdataForWeather
    : getDynamicReportdata;
  const getFarmTableDataAPI = useApi(apiHook);
  const [tableData, setTableData] = useState(null);
  const [filteredTableData, setFilteredTableData] = useState(null); // Data to display
  const [pageSize, setPageSize] = useState(limit);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [canNextPage, setCanNextPage] = useState(true);
  const [filters, setFilters] = useState({});
  const [globalfilters, setGlobalFilters] = useState({
    search_text: '',
    start_date: '',
    end_date: ''
  });
  const [cursor, setCursor] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deboucedApiCall, setDebouncedApiCall] = useState(false);
  const [isGlobalSearchActive, setIsGlobalSearchActive] = useState(true); // this state is to check weather which one of the search filter is active(global or normal).it is made to fix the issue where if there is no data while doing normal search(front-end search) the whole table is getting disappered.
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState(null);
  const [exportKey, setExportKey] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});

  // Add a ref for the refresh function that can be exposed
  const refreshTableRef = useRef(null);
  // Define a function to directly call the API to refresh data and update the table
  const refreshTable = useCallback(() => {
    console.log('Directly refreshing DynamicReportTable data with API call');

    // Reset page index to start from the first page
    setPageIndex(0);

    // Prepare the parameters for the API call
    // Make sure we use the current pageSize value
    const limit = pageSize;
    console.log(`Using current page size for refresh: ${limit}`);

    const cursor = 0; // Start from the beginning
    const filter = globalfilters;
    // Always use DIGITIZATION service for refreshing data
    const refreshService = service;

    // Return a promise to allow chaining and tracking completion
    return new Promise((resolve, reject) => {
      // Define and immediately execute the async function
      (async () => {
        try {
          console.log(`Making API request with limit: ${limit}`);
          let response;

          if (forWeather && district_ids) {
            response = await getFarmTableDataAPI.request({
              apiPath,
              cursor,
              limit, // Use current page size
              tenant,
              filter,
              district_ids,
              service: refreshService // Always include service
            });
          } else if (!forWeather && dropdownFilter) {
            response = await getFarmTableDataAPI.request({
              apiPath,
              cursor,
              limit, // Use current page size
              tenant,
              filter,
              service: refreshService, // Use DIGITIZATION service
              dropdownOptions,
              selectedValues,
              payloadmode
            });
          } else if (!forWeather) {
            response = await getFarmTableDataAPI.request({
              apiPath,
              cursor,
              limit, // Use current page size
              tenant,
              filter,
              service: refreshService // Always include service
            });
          }

          // Wait a brief moment for the API data to be available
          setTimeout(() => {
            // Explicitly process response data immediately to update UI
            const data = getFarmTableDataAPI.data;
            if (data && data.results) {
              const tabledata = data.results;
              console.log(
                'Successfully fetched new data in refreshTable:',
                tabledata.data?.length || 0,
                'rows with page size',
                limit
              );

              // Immediately update the table data
              setTableData(tabledata.data);
              setFilteredTableData(tabledata.data);
              setRowCount(tabledata.total || 0);
              setCanNextPage(tabledata.remaining_records > 0);

              // Also update export data if needed
              filterExportData(tabledata.data);

              // Resolve the promise with the response
              resolve(response);
            } else {
              console.warn('No results found in API response');
              resolve(response); // Still resolve to prevent hanging promises
            }
          }, 100); // Short delay to ensure data is available
        } catch (error) {
          console.error('Error in refreshTable:', error);
          reject(error);
        }
      })();
    });
  }, [
    pageSize,
    globalfilters,
    forWeather,
    district_ids,
    apiPath,
    tenant,
    dropdownFilter,
    service,
    dropdownOptions,
    selectedValues,
    payloadmode,
    getFarmTableDataAPI
  ]);

  // Expose the refresh function via ref and global window property
  useEffect(() => {
    refreshTableRef.current = refreshTable;

    if (typeof window !== 'undefined') {
      window.refreshDynamicReportTable = refreshTable;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.refreshDynamicReportTable;
      }
    };
  }, [refreshTable]);

  useEffect(() => {
    const shouldCallAPI =
      globalfilters.search_text ||
      (globalfilters.start_date == '' && globalfilters.end_date == '') ||
      (globalfilters.start_date && globalfilters.end_date);
    if (shouldCallAPI) {
      let limit = pageSize;
      let cursor = pageIndex * pageSize;
      let filter = globalfilters;
      setCursor(cursor);
      if (forWeather && district_ids) {
        getFarmTableDataAPI.request({
          apiPath,
          cursor,
          limit,
          tenant,
          filter,
          district_ids,
          service
        });
      }
      if (!forWeather && dropdownFilter) {
        getFarmTableDataAPI.request({
          apiPath,
          cursor,
          limit,
          tenant,
          filter,
          service,
          dropdownOptions,
          selectedValues,
          payloadmode
        });
      } else if (!forWeather) {
        getFarmTableDataAPI.request({
          apiPath,
          cursor,
          limit,
          tenant,
          filter,
          service
        });
      }
    }
  }, [pageIndex, deboucedApiCall, pageSize, apiPath, selectedValues]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      if (dropdownFilter && Array.isArray(dropdownData)) {
        const allOptions = {};

        for (const item of dropdownData) {
          const response = await getDropdownFilterData({
            apiPath: item.apiPath,
            service
          });

          const options = response.data?.results || [];
          allOptions[item.key] = options;
        }

        setDropdownOptions(allOptions); // Save all dropdowns by key
      }
    };

    fetchDropdowns();
  }, [dropdownFilter, dropdownData]);

  const filterExportData = data => {
    // this function is to filter the export Data i.e to filter sections field in the report
    const filteredData = data?.map(obj => {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([key]) =>
            !key.startsWith('section') && key !== 'action' && key !== 'id'
        )
      );
    });
    setExportData(filteredData);
  };

  useEffect(() => {
    if (!getFarmTableDataAPI.data) {
      console.log('No data received from API yet');
      return;
    }

    const tabledata = get(getFarmTableDataAPI, 'data.results', []);
    const exportKey = get(getFarmTableDataAPI, 'data.results.export_key', '');
    const rowTotal = get(getFarmTableDataAPI, 'data.results.total', 0);
    const requestParams = get(getFarmTableDataAPI, 'data.request', {});

    setExportKey(exportKey);
    setRowCount(rowTotal);
    setCanNextPage(tabledata.remaining_records > 0);

    if (Array.isArray(tabledata.data)) {
      // Update both table data states
      setTableData(tabledata.data);
      setFilteredTableData(tabledata.data);
      filterExportData(tabledata.data);
    } else {
      console.warn(
        'Received invalid data format from API, tabledata.data is not an array:',
        tabledata.data
      );
    }
  }, [getFarmTableDataAPI.data, pageSize]);

  const flattenedData = tableData;

  const columns =
    flattenedData?.length > 0
      ? [
          ...Object.keys(flattenedData[0])
            .filter(
              key =>
                !key.includes('_color') && key !== 'farm_id' && key !== 'id'
            )
            .map((key, index) => ({
              Header: key
                .replace(/_/g, ' ')
                .replace(/(^\w|\b\w(?![^()]*\)))/g, c => c.toUpperCase()),
              accessor: key,
              Cell: ({ value, row }) => {
                // Dynamically handle NA or null values
                const colorKey = `${key}_color`;
                let displayValue =
                  typeof value === 'object' && value !== null
                    ? ' - '
                    : value === 'NA' || value === null
                    ? ' - '
                    : value;
                if (
                  typeof value === 'string' &&
                  value.includes('<a') &&
                  value.includes('href=')
                ) {
                  const match = value.match(/href="([^"]+)"/);
                  const href = match?.[1];
                  if (href) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: row.original[colorKey],
                          textDecoration: 'underline'
                        }}
                      >
                        View Map
                      </a>
                    );
                  }
                }

                return (
                  <span style={{ color: row.original[colorKey] || 'inherit' }}>
                    {displayValue || ''}
                  </span>
                );
              }
            }))
        ]
      : [];

  const customStyles = {
    control: provided => ({
      ...provided,
      minHeight: '80px',
      backgroundColor: 'blue'
    }),
    valueContainer: provided => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflowY: 'auto',
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      maxWidth: '200px',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      maxHeight: '90px'
    }),

    multiValue: provided => ({
      ...provided,
      backgroundColor: 'yellow',
      height: 'fit-content'
    }),
    multiValueLabel: provided => ({
      ...provided
    })
  };

  const handlePageChange = newPageIndex => {
    setPageIndex(newPageIndex);
  };

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize);
    setPageIndex(0);
    setTableData(null);
  };

  // Handle filter changes for specific columns
  const handleFilterChange = (columnId, value) => {
    setIsGlobalSearchActive(false);
    setFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  useEffect(() => {
    if (!tableData || Object.keys(filters).length === 0) {
      setFilteredTableData(tableData); // Reset to original if no filters
      return;
    }
    const filtered = tableData.filter(row =>
      Object.entries(filters).every(([columnId, filterValue]) => {
        const cellValue = row[columnId]?.toString().toLowerCase() || '';
        const filterValueLower = filterValue.toLowerCase();
        return cellValue.startsWith(filterValueLower);
      })
    );
    setRowCount(filtered.length);
    setFilteredTableData(filtered);
  }, [filters, tableData]);

  const debouncedApiTrigger = useCallback(
    debounce(() => setDebouncedApiCall(prev => !prev), 500),
    []
  );

  const handleGlobalFilterChange = (columnId, value) => {
    if (value) {
      setIsGlobalSearchActive(true);
    } else {
      setIsGlobalSearchActive(false);
    }
    setGlobalFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
    debouncedApiTrigger();
  };

  const handleGlobalSearch = e => {
    const searchValue = e.target.value;
    handleGlobalFilterChange('search_text', searchValue);
  };

  const handleDateChange = (date, field) => {
    if (date) {
      const day = String(date.getDate()).padStart(2, '0'); // Ensures 2 digits
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const year = date.getFullYear();
      const formattedDate = `${year}-${month}-${day}`; // Format: dd-mm-yyyy
      handleGlobalFilterChange(field, formattedDate);
    } else {
      handleGlobalFilterChange(field, '');
    }
  };

  const handleAction = (action, rowData) => {
    if (action === 'edit') {
      onEdit(action, rowData); // Trigger onEdit passed as prop
    } else if (action === 'add') {
      onAdd(action, rowData); // Trigger onAdd passed as prop
    } else if (action === 'view') {
      const farmerId = rowData.id; // Assuming rowData contains the farmerId
      setShowViewModal(true); // Show the modal if needed
      setSelectedFarmerId(farmerId); // Store the farmerId
    } else if (action === 'viewData') {
      // Assuming rowData contains the data you want to display in the modal
      const modalData = rowData; // You can modify this to extract specific data if needed
      setShowButtonModal(true); // Show the modal for the button action
      setSelectedModalData(modalData); // Store the data to be displayed in the modal
    } else if (action == 'viewDetails') {
      onViewDetails(action, rowData);
    } else if (action === 'download') {
      handleDownloadAll(rowData);
    }
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update windowWidth on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    // Clean up the event listener when the component is unmounted
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Breakpoint for medium screens in pixels
  const isMediumScreen = windowWidth >= 768;

  const handleDownloadAll = async rowData => {
    const logId = rowData.id;
    const filenameKey = Object.keys(rowData).find(
      key => key.trim().toLowerCase() === 'filename'
    );
    const filename = rowData[filenameKey]?.trim() || 'export.xlsx';
    const responseData = await downloadExportFile(logId);
    if (!responseData || !responseData.success || !responseData.results?.url) {
      throw new Error('Invalid response from downloadExportFile.');
    }
    const downloadUrl = responseData.results.url;
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch file. Status: ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {isMediumScreen ? (
        <TableComponent
          setExportData
          apiPath={apiPath}
          forWeather
          district_ids
          errorMsg={errorMsg}
          responsive
          cardTittles
          dateRangePicker={dateRangePicker}
          globalSearch
          limit
          handleAction={handleAction}
          buttons={buttons}
          title={title}
          handleDownload={handleDownload}
          showHeader={showHeader}
          showFooter={showFooter}
          dateFilter={dateFilter}
          exportKey={exportKey}
          dropdownFilter={dropdownFilter}
          dropdownData={dropdownData}
          dropdownOptions={dropdownOptions}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          service={service}
        />
      ) : (
        <>
          <div className="">
            <Row className="d-flex justify-content-end mb-2">
              <Col className="text-end">
                {buttons
                  ?.filter(button => button.enabled)
                  .map((button, index) => (
                    <Button
                      key={index}
                      variant="primary"
                      onClick={() =>
                        navigate(`/${tenant}${button.redirectPath}`)
                      }
                      className={index > 0 ? 'ms-2' : ''} // Apply spacing only to subsequent buttons
                    >
                      {button.buttonName}
                    </Button>
                  ))}
              </Col>
            </Row>
            <Card>
              {showHeader && (
                <FalconCardHeader
                  title={title}
                  titleTag="h5"
                  light={true}
                  endEl={
                    <div className="d-flex justify-content-end my-auto">
                      <CardDropdown
                        onExport={handleDownload}
                        exportKey={exportKey}
                      />
                    </div>
                  }
                />
              )}
              <CardBody>
                <ServerSideTableWrapper
                  key={`${pageSize}-${pageIndex}`}
                  columns={columns}
                  data={filteredTableData || flattenedData}
                  pagination
                  sortable
                  perPage={pageSize}
                  className="min-vh-50 py-5"
                  stickyColumns={[]}
                >
                  <div className="d-md-none mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                  </div>

                  <div
                    className={`d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 ${
                      showFilters ? '' : 'd-none d-md-flex'
                    }`}
                  >
                    {globalSearch ? (
                      <input
                        type="text"
                        placeholder="Search..."
                        className="form-control mb-3 mb-md-0 w-100 w-md-25"
                        value={globalfilters.search_text}
                        onChange={handleGlobalSearch}
                      />
                    ) : (
                      <></>
                    )}

                    <div className="d-flex flex-column flex-md-row gap-3 w-100 w-md-auto">
                      <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
                        {dateFilter && (
                          <DatePicker
                            selected={
                              globalfilters.start_date
                                ? new Date(globalfilters.start_date)
                                : null
                            }
                            onChange={date =>
                              handleDateChange(date, 'start_date')
                            }
                            placeholderText="Start Date"
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                            maxDate={
                              globalfilters.end_date
                                ? new Date(globalfilters.end_date)
                                : new Date() // Default to today's date if end_date is not set
                            }
                          />
                        )}
                      </div>

                      <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
                        {dateFilter && (
                          <DatePicker
                            selected={
                              globalfilters.end_date
                                ? new Date(globalfilters.end_date)
                                : null
                            }
                            onChange={date =>
                              handleDateChange(date, 'end_date')
                            }
                            placeholderText="End Date"
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                            minDate={
                              globalfilters.start_date
                                ? new Date(globalfilters.start_date)
                                : null // No minimum date if start_date is not set
                            }
                            maxDate={new Date()}
                          />
                        )}
                      </div>

                      {(globalfilters.start_date || globalfilters.end_date) && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary align-self-md-start"
                          onClick={() => {
                            handleDateChange('', 'start_date');
                            handleDateChange('', 'end_date');
                          }}
                        >
                          Clear
                        </button>
                      )}
                      <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
                        {dropdownFilter &&
                          dropdownData.map(drop => (
                            <Col key={drop.key} lg={12}>
                              <Select
                                options={dropdownOptions[drop.key] || []}
                                isClearable={true}
                                placeholder={drop.label}
                                classNamePrefix="react-select"
                                value={selectedValues[drop.key] || null}
                                onChange={selected => {
                                  setSelectedValues(prev => ({
                                    ...prev,
                                    [drop.key]: selected
                                  }));
                                }}
                                isMulti={drop.isMulti}
                                closeMenuOnSelect={!drop.isMulti}
                                styles={{
                                  ...customStyles,
                                  control: base => ({
                                    ...base,
                                    minWidth: '200px',
                                    maxWidth: '400px',
                                    width: '100%'
                                  }),
                                  option: (provided, state) => ({
                                    ...provided,
                                    color: state.isDisabled
                                      ? '#d3d3d3'
                                      : '#000',
                                    fontWeight: state.isDisabled
                                      ? 'lighter'
                                      : 'bold',
                                    cursor: state.isDisabled
                                      ? 'not-allowed'
                                      : 'pointer'
                                  })
                                }}
                              />
                            </Col>
                          ))}
                      </div>
                    </div>
                  </div>

                  {rowCount > 0 || !isGlobalSearchActive ? (
                    <ServerSideTable
                      key={`${pageSize}-${pageIndex}`}
                      responsive={responsive}
                      cardTittles={cardTittles}
                      table
                      headerClassName="bg-200 text-900 text-nowrap align-middle"
                      rowClassName="align-middle white-space-nowrap"
                      tableProps={{
                        bordered: true,
                        striped: true,
                        className: 'fs--1 mb-0'
                      }}
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      showFilterRow={showFilter}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      cursor={cursor}
                      clientSideSearch={true}
                      handleAction={handleAction}
                      isGlobalSearchActive={isGlobalSearchActive}
                    />
                  ) : (
                    <h1
                      className="text-center my-5"
                      style={{
                        color: '#6c757d',
                        fontSize: '1.5rem',
                        opacity: 0.7
                      }}
                    >
                      {globalfilters.search_text != ''
                        ? `"${globalfilters.search_text}", not found!`
                        : errorMsg !== '' && errorMsg !== undefined
                        ? errorMsg
                        : 'No data added!'}
                    </h1>
                  )}
                  {/* )} */}
                  {showFooter ? (
                    <div className="sticky bottom-0 bg-white border-t">
                      <ServerSideTableFooter
                        rowCount={rowCount}
                        pageSize={pageSize}
                        pageIndex={pageIndex}
                        setPageSize={handlePageSizeChange}
                        canPreviousPage={pageIndex > 0}
                        canNextPage={canNextPage}
                        nextPage={() => handlePageChange(pageIndex + 1)}
                        previousPage={() =>
                          handlePageChange(Math.max(0, pageIndex - 1))
                        }
                        rowsPerPageSelection
                        navButtons
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </ServerSideTableWrapper>
              </CardBody>
            </Card>
          </div>
        </>
      )}
      <>
        {showViewModal && (
          <ViewComponent
            farmerId={selectedFarmerId}
            onClose={() => setShowViewModal(false)} // You might want to close the modal
          />
        )}
        {showButtonModal && selectedModalData?.extra?.request_id && (
          <DeliveryDetailsModal
            show={showButtonModal}
            onClose={refreshNeeded => {
              console.log(
                'DeliveryDetailsModal closed with refreshNeeded:',
                refreshNeeded
              );
              setShowButtonModal(false);
              // If modal was closed with refreshNeeded flag, trigger a refresh
              if (refreshNeeded) {
                console.log('Refresh triggered from modal close');
                // Ensure the refresh happens after the modal state update
                setTimeout(() => {
                  refreshTable()
                    .then(() => {
                      console.log('Refresh completed from modal close');
                    })
                    .catch(err => {
                      console.error(
                        'Error during refresh from modal close:',
                        err
                      );
                    });
                }, 0);
              }
            }}
            selectedModalData={selectedModalData}
            refreshTable={refreshTable}
          />
        )}
        {showButtonModal && !selectedModalData?.extra?.request_id && (
          <DynamicModal
            show={true}
            onClose={() => setShowButtonModal(false)}
            modalData={selectedModalData}
          />
        )}
      </>
    </>
  );
};

DynamicReportTable.propTypes = {
  data: PropTypes.array.isRequired,
  deletePorjectAPI: PropTypes.object.isRequired,
  startProcessingAPI: PropTypes.object.isRequired
};

ServerSideTableWrapper.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      Cell: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          value: PropTypes.func.isRequired,
          row: PropTypes.shape({
            original: PropTypes.object.isRequired
          }).isRequired
        })
      ])
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.bool,
  perPage: PropTypes.number
};

export default DynamicReportTable;
